// SessionTimeout - enforces the organisation's sign-in policy in the console.
// Mounted inside ConsoleApp so it does NOT touch the shared auth provider
// (which admin also uses). This is REAL enforcement: it calls
// supabase.auth.signOut() for real.
//
// G360_K580 - asks my_security_check (migration 0310) when the console
// opens and every 5 minutes:
//   - idle timeout: owners and admins can have a shorter one;
//   - maximum session: signed out when the session is older than the
//     organisation allows, active or not;
//   - sign-in policy: a person whose sign-in method, email domain or Entra
//     tenant the organisation does not allow is told why and signed out.
// If the check cannot be asked (before 0310 is run, or offline) it falls
// back to the plain idle timeout it always had - an outage never signs
// anyone out. Govern360 staff opening an organisation for support are
// exempt (decided by the database).
import { useEffect, useRef, useState } from 'react';
import { useAuth, supabase } from '@govern360/shared';
import { getSecuritySettings, myCheck } from '../lib/securityApi.js';

const RECHECK = 5 * 60 * 1000;
const WHY = {
  method: (c) => 'Your organisation allows sign-in only through its identity provider. You signed in with ' + ({ azure: 'Microsoft', google: 'Google', email: 'an email link or password' }[c.provider] || 'a method it does not allow') + '.',
  domain: (c) => 'Your organisation allows only its own email domains' + (c.domain ? ', and ' + c.domain + ' is not one of them' : '') + '.',
  tenant: () => 'Your organisation allows Microsoft sign-ins only from its own Microsoft Entra tenant, and this account belongs to another.',
};

export default function SessionTimeout() {
  const { tenant, isAuthenticated } = useAuth();
  const timer = useRef(null);
  const hard = useRef(null);
  const minutes = useRef(1440);
  const [blocked, setBlocked] = useState(null);
  const [others, setOthers] = useState([]);

  // A person who also belongs to another organisation can switch to it
  // instead of being stuck: sign-in lands on the active organisation.
  useEffect(() => {
    if (!blocked || !tenant?.id) return;
    supabase.rpc('my_orgs').then(({ data }) => {
      setOthers((Array.isArray(data) ? data : []).filter((o) => o && o.tenant_id && o.tenant_id !== tenant.id));
    }, () => setOthers([]));
  }, [blocked, tenant?.id]);
  async function switchTo(id) {
    const { error } = await supabase.rpc('set_active_org', { p_tenant_id: id });
    if (!error) { window.location.hash = '#dashboard'; window.location.reload(); }
  }

  useEffect(() => {
    if (!isAuthenticated || !tenant?.id) return undefined;
    let cancelled = false;
    let poll = null;

    function doLogout() {
      // real sign-out; clears the Supabase session
      supabase.auth.signOut().finally(() => {
        window.location.hash = '#dashboard';
        window.location.reload();
      });
    }
    function arm() {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(doLogout, minutes.current * 60 * 1000);
    }
    function onActivity() { arm(); }

    async function check(first) {
      const c = await myCheck(tenant.id);
      if (cancelled) return;
      if (!c) {
        if (first) {
          const { data } = await getSecuritySettings(tenant.id);
          if (cancelled) return;
          minutes.current = (data && data.session_timeout_min) || 1440;
          arm();
        }
        return;
      }
      if (c.allowed === false) { setBlocked(c); return; }
      const m = Number(c.idle_min) || 1440;
      if (first || m !== minutes.current) { minutes.current = m; arm(); }
      if (hard.current) { clearTimeout(hard.current); hard.current = null; }
      const started = Date.parse(c.session_started_at || c.last_sign_in_at || '');
      if (c.absolute_hours && Number.isFinite(started)) {
        const left = started + Number(c.absolute_hours) * 3600000 - Date.now();
        if (left <= 0) { doLogout(); return; }
        hard.current = setTimeout(doLogout, Math.min(left, 2147483000));
      }
    }

    check(true);
    poll = setInterval(() => check(false), RECHECK);
    const evs = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    evs.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      if (timer.current) clearTimeout(timer.current);
      if (hard.current) clearTimeout(hard.current);
      evs.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [isAuthenticated, tenant?.id]);

  if (!blocked) return null;
  const say = (WHY[blocked.reason] || WHY.method)(blocked);
  return (
    <div role="alertdialog" aria-modal="true" data-testid="sec-blocked" style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(32,33,36,.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="card" style={{ maxWidth: 460, padding: 24, borderTop: '3px solid var(--g-danger, #b31412)' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Sign in another way</h2>
        <p style={{ fontSize: 13.5, lineHeight: 1.55, margin: '0 0 8px' }}>{say}</p>
        <p style={{ fontSize: 12.5, color: 'var(--g-text-2)', lineHeight: 1.5, margin: '0 0 16px' }}>Sign out, then sign in with the account your organisation uses. If you think this is wrong, ask an owner or admin of {tenant && tenant.name ? tenant.name : 'your organisation'}.</p>
        {others.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '0 0 12px' }}>
            {others.slice(0, 4).map((o) => <button key={o.tenant_id} className="btn outline" data-testid="sec-blocked-switch" onClick={() => switchTo(o.tenant_id)}>Open {o.name || o.tenant_name || 'another organisation'}</button>)}
          </div>
        )}
        <button className="btn primary" data-testid="sec-blocked-out" onClick={() => { supabase.auth.signOut().finally(() => { window.location.hash = '#dashboard'; window.location.reload(); }); }}>Sign out</button>
      </div>
    </div>
  );
}
